import { type ReactNode, useCallback, useMemo, useState } from "react";
import { DialogContext, type DialogOptions } from "./dialogContext";
import Dialog from "@/components/dialog/Dialog";

const DialogProvider = ({ children }: { children: ReactNode }) => {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState<string | React.ReactNode | undefined>();
  const [content, setContent] = useState<
    string | React.ReactNode | undefined
  >();
  const [confirmText, setConfirmText] = useState("確認");
  const [cancelText, setCancelText] = useState("取消");
  const [onConfirm, setOnConfirm] = useState<
    (() => void | Promise<void>) | undefined
  >();
  const [onCancel, setOnCancel] = useState<(() => void) | undefined>();
  const [isShowCloseIcon, setIsShowCloseIcon] = useState(true);

  const openDialog = useCallback((options: DialogOptions) => {
    setTitle(options.title);
    setContent(options.content);
    setConfirmText(options.confirmText || "確認");
    setCancelText(options.cancelText || "取消");
    setOnConfirm(() => options.onConfirm);
    setOnCancel(() => options.onCancel);
    setIsShowCloseIcon(options.isShowCloseIcon ?? true);
    setOpen(true);
  }, []);

  const closeDialog = useCallback(() => {
    setOpen(false);
  }, []);

  const handleConfirm = useCallback(async () => {
    if (onConfirm) {
      await onConfirm();
    }
    closeDialog();
  }, [onConfirm, closeDialog]);

  const handleCancel = useCallback(() => {
    if (onCancel) {
      onCancel();
    }
    closeDialog();
  }, [onCancel, closeDialog]);

  const contextValue = useMemo(
    () => ({
      open,
      title,
      content,
      confirmText,
      cancelText,
      onConfirm,
      onCancel,
      openDialog,
      closeDialog,
    }),
    [
      open,
      title,
      content,
      confirmText,
      cancelText,
      onConfirm,
      onCancel,
      openDialog,
      closeDialog,
    ]
  );

  return (
    <DialogContext.Provider value={contextValue}>
      {children}
      <Dialog
        open={open}
        title={title}
        content={content}
        confirmText={confirmText}
        cancelText={cancelText}
        onConfirm={handleConfirm}
        onCancel={onCancel ? handleCancel : undefined}
        onClose={onCancel ? handleCancel : handleConfirm}
        isShowCloseIcon={isShowCloseIcon}
      />
    </DialogContext.Provider>
  );
};

export default DialogProvider;
